// src/pages/SesionVivaPage.js
//
// Antes "LiveSessionPage.js" — reescrito por dos razones:
//   1. Nombre: ya no dice "Master Talks" en ninguna parte.
//   2. Arquitectura: NO usa video propio (WebRTC/useWebRTC/VideoGrid/
//      señalización). El video real vive en Google Meet; el link se
//      genera automáticamente en el backend al crear la sesión
//      (GoogleCalendarEducaTService). Esta página solo muestra el
//      detalle de la sesión, materiales, y un botón que abre el Meet
//      en una pestaña nueva mientras registra la asistencia.
//
// El módulo WebRTC (useWebRTC.js, VideoGrid.js, VideoControls.js,
// signalingServer.js) queda deprecado — no se borra por si se
// reutiliza en otro contexto, pero esta página ya no depende de él.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Skeleton,
  Alert,
  Avatar,
} from '@mui/material';
import {
  VideoCall,
  FiberManualRecord,
  Schedule,
  RecordVoiceOver,
  InsertDriveFile,
  Download,
  ArrowBack,
  PlayCircleOutline,
  Groups,
} from '@mui/icons-material';
import { sesionOnlineService } from '../services/api';

const TIPO_LABELS = {
  ClaseEnVivo: 'Clase en vivo',
  SesionPractica: 'Sesión práctica',
  PresentacionProyecto: 'Presentación de proyecto',
  SesionOrientacion: 'Sesión de orientación',
};

function SesionVivaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sesion, setSesion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await sesionOnlineService.getById(id);
    setSesion(data);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleJoin = async () => {
    if (!sesion?.meetLink) return;
    setJoining(true);
    try {
      const result = await sesionOnlineService.registrarAsistencia(id);
      window.open(result?.meetLink || sesion.meetLink, '_blank', 'noopener,noreferrer');
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="rounded" height={300} />
      </Container>
    );
  }

  if (!sesion) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>Sesión no encontrada</Typography>
        <Button component={Link} to="/sesiones" startIcon={<ArrowBack />}>Volver a Sesiones</Button>
      </Container>
    );
  }

  const isLive = sesion.status === 'EnVivo';
  const isFinished = sesion.status === 'Finalizada';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/sesiones')}
        sx={{ mb: 2 }}
      >
        Volver a Sesiones
      </Button>

      <Card>
        <Box
          sx={{
            p: 4,
            background: isLive
              ? 'linear-gradient(135deg, #e94560, #ff6b6b)'
              : 'linear-gradient(135deg, #3b82f6, #06b6d4)',
            color: 'white',
          }}
        >
          <Chip
            icon={isLive ? <FiberManualRecord /> : <VideoCall />}
            label={isLive ? 'EN VIVO' : TIPO_LABELS[sesion.type] || sesion.type}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, mb: 2 }}
          />
          <Typography variant="h4" fontWeight={700} gutterBottom>{sesion.title}</Typography>
          <Typography sx={{ opacity: 0.9 }}>{sesion.courseTitle}</Typography>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {sesion.description && (
            <Typography color="text.secondary" sx={{ mb: 3 }}>{sesion.description}</Typography>
          )}

          <List dense>
            <ListItem disableGutters>
              <ListItemIcon><RecordVoiceOver /></ListItemIcon>
              <ListItemText
                primary={sesion.instructorName || sesion.externalSpeaker}
                secondary="Instructor / ponente"
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon><Schedule /></ListItemIcon>
              <ListItemText
                primary={new Date(sesion.startAt).toLocaleString('es-CO', { dateStyle: 'full', timeStyle: 'short' })}
                secondary={`Duración: ${sesion.durationMinutes} minutos`}
              />
            </ListItem>
            <ListItem disableGutters>
              <ListItemIcon><Groups /></ListItemIcon>
              <ListItemText
                primary={`${sesion.enrolled} inscritos`}
                secondary={sesion.maxParticipants ? `Máximo ${sesion.maxParticipants} participantes` : 'Sin límite de participantes'}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 3 }} />

          {/* Acción principal — unirse via Google Meet */}
          {isFinished && sesion.recordingUrl ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Esta sesión ya finalizó. Puedes ver la grabación abajo.
            </Alert>
          ) : isFinished ? (
            <Alert severity="info">Esta sesión ya finalizó y no tiene grabación disponible.</Alert>
          ) : sesion.meetLink ? (
            <Button
              variant="contained"
              size="large"
              fullWidth
              color={isLive ? 'error' : 'primary'}
              startIcon={<VideoCall />}
              disabled={joining}
              onClick={handleJoin}
            >
              {joining ? 'Abriendo Google Meet…' : isLive ? 'Unirse ahora a Google Meet' : 'Abrir enlace de Google Meet'}
            </Button>
          ) : (
            <Alert severity="warning">
              El enlace de Google Meet para esta sesión aún no está disponible. Contacta a tu instructor.
            </Alert>
          )}

          {sesion.recordingUrl && (
            <Button
              variant="outlined"
              fullWidth
              sx={{ mt: 2 }}
              startIcon={<PlayCircleOutline />}
              component="a"
              href={sesion.recordingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver grabación de la sesión
            </Button>
          )}

          {/* Materiales */}
          {sesion.materials?.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Materiales</Typography>
              <List dense>
                {sesion.materials.map((m) => (
                  <ListItem
                    key={m.id}
                    disableGutters
                    secondaryAction={
                      <Button
                        size="small"
                        startIcon={<Download />}
                        component="a"
                        href={m.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Descargar
                      </Button>
                    }
                  >
                    <ListItemIcon><InsertDriveFile /></ListItemIcon>
                    <ListItemText primary={m.name} />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {sesion.notes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Notas de la sesión</Typography>
              <Typography color="text.secondary">{sesion.notes}</Typography>
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default SesionVivaPage;
